// CompassionToday.tsx — the body of the self-compassion daily view: one thing to
// do today. Either today's exercise/meditation (or the next intro), a "done for
// today, come back tomorrow" rest, or a course-complete note. The week/day/
// days-left header is rendered by HomeScreen above this.
import { Display, Body, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';
import { BrandMark } from '@/components/icons';
import { clipUrl } from '@/data/guideMessages';
import type { Step } from '@/types';
import type { DailyState } from '@/services/compassionDaily';

function cover(step: Step): string | undefined {
  return step.poster_url || step.image_url || undefined;
}

export function CompassionToday({ daily, onOpenStep }: { daily: DailyState; onOpenStep: (s: Step) => void }) {
  if (daily.finished) {
    return (
      <div style={{ textAlign: 'center', padding: '34px 18px' }}>
        <BrandMark size={30} />
        <Display parts={{ pre: 'You have completed ', em: 'the course', post: '.' }} size={20} style={{ marginTop: 14 }} />
        <Body dim size={13.5} style={{ marginTop: 8 }}>
          Self-compassion is a practice for life — return to any day, or to a meditation, whenever you need it.
        </Body>
      </div>
    );
  }

  if (daily.locked) {
    const done = daily.doneToday;
    return (
      <div style={{ padding: '22px 18px', borderRadius: 18, border: '1px solid rgba(29,158,117,0.28)', background: 'linear-gradient(180deg, rgba(29,158,117,0.10), rgba(29,158,117,0.03))', textAlign: 'center' }}>
        {/* Craig's slow-down presence — one small practice a day, no pushing on. */}
        <img
          src={clipUrl('invite-to-slowdown')}
          alt=""
          aria-hidden="true"
          style={{ width: 116, height: 142, objectFit: 'contain', objectPosition: 'center', display: 'block', margin: '0 auto 6px' }}
        />
        <Display parts={{ pre: "That's enough for ", em: 'today', post: '.' }} size={20} />
        <Body dim size={13.5} style={{ marginTop: 8, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto' }}>
          {daily.held
            ? `This practice asks to be repeated over a few days. That's day ${daily.held.done} of ${daily.held.need} done — come back tomorrow to keep going.`
            : `One small practice a day is the whole idea. ${daily.dayLabel ? `${daily.dayLabel} will be waiting for you tomorrow.` : 'Come back tomorrow for the next one.'}`}
        </Body>
        {done && (
          <Pressable
            as="button"
            onClick={() => onOpenStep(done)}
            ariaLabel="Reopen today's exercise"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 16, padding: '11px 18px', borderRadius: 12, border: '1px solid rgba(29,158,117,0.4)', background: 'var(--card-top)', color: 'var(--green-deep)', cursor: 'pointer', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, fontWeight: 700 }}
          >
            <Icon name="reflections" size={15} /> Revisit today's practice
          </Pressable>
        )}
      </div>
    );
  }

  const step = daily.step;
  if (!step) return null;
  const isVideo = step.type === 'video';
  const img = cover(step);

  return (
    <Pressable onClick={() => onOpenStep(step)} ariaLabel={`Open ${daily.dayLabel ?? 'today’s lesson'}`}>
      <div style={{ borderRadius: 'var(--r-card)', overflow: 'hidden', border: '1px solid var(--hair)', background: 'var(--card-top)', boxShadow: 'var(--edge), var(--sh1)' }}>
        {img && (
          <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: 'var(--paper)' }}>
            <img src={img} alt="" aria-hidden="true" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            {isVideo && (
              <span aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(20,14,8,0.18)' }}>
                <span style={{ width: 46, height: 46, borderRadius: 999, background: 'rgba(255,250,240,0.94)', color: 'var(--green-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                  <Icon name="play" size={20} />
                </span>
              </span>
            )}
          </div>
        )}
        <div style={{ padding: '15px 16px 17px' }}>
          {daily.dayLabel && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-deep)', marginBottom: 6 }}>
              {daily.held ? `${daily.dayLabel} · day ${daily.held.done + 1} of ${daily.held.need}` : `${daily.dayLabel} · today`}
            </div>
          )}
          <Display parts={step.title} size={19} lh={1.18} />
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 700 }}>
            {isVideo ? 'Play' : daily.dayLabel ? "Begin today's practice" : 'Start here'} <Icon name="chevRight" size={15} />
          </div>
        </div>
      </div>
    </Pressable>
  );
}
